import React, {useEffect, useState, useMemo} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
//local
import {Member} from "./components/Member";
import {MembersSummary} from "./components/MembersSummary";
import {MemberSettingsModal} from "./components/MemberSettingsModal";
import {showSuccess} from "helpers/notifications.helper";
import {VideoInstructionLink} from "components/VideoInstructionLink";
import {getFullName} from "helpers/helper";

export const Members = inject("store")(
  observer(({store: {organization, auth, buyer}}) => {
    const {organizationMembers, organizationDetail, subscriptions, subscriptionRoles} =
      organization;
    const {t} = useTranslation();
    const [projectsList, setProjectsList] = useState([]);
    const [isMemberSettingsModal, setMemberSettingsModal] = useState(null);
    const {activeOrganizationId, user, clientRole, isStaff} = auth;

    const members = useMemo(() => organizationMembers, [organizationMembers]);

    const onUpdateMember = (userId, value) =>
      organization.updateMember(userId, value).then(showSuccess);

    const removeMember = (userId) =>
      organization.removeMember(userId).then(() => {
        setMemberSettingsModal(null);
        organization.getOrganizationMembers();
      });

    useEffect(() => {
      buyer.getSimplifiedProjects().then(({data}) =>
        setProjectsList(
          data
            .filter((item) => !!item.name)
            .map((project) => ({
              value: project.id,
              label: project.name,
            }))
        )
      );
    }, [organization, activeOrganizationId, buyer]);

    return (
      <div className="members">
        <div className="header-wrapper">
          {t("PEOPLE_IN")}
          {user?.ActiveOrganization?.name}
          <VideoInstructionLink
            label={`VIDEO_LINK_SETUP_MEMBERS_LABEL`}
            link={`VIDEO_LINK_SETUP_MEMBERS_LINK`}
          />
        </div>

        {!!members.length &&
          members.map((member, index) => (
            <Member
              key={member.UserId}
              index={index}
              userInfo={member.User}
              role={member.role}
              subscriptionRole={subscriptionRoles.find(
                (item) => item.id === member.SubscribeRoleId
              )}
              limitProjectNames={projectsList
                .filter((project) => member.limitProjectIds.includes(project.value))
                .map((project) => project.label)}
              availableIn={member.availableIn}
              ownMember={member.UserId === user?.id}
              isChecked={member.isRfpEmailNotifications}
              userId={member.UserId}
              organizationId={activeOrganizationId}
              individualSubscriptions={
                member.User.SubscribeUserUnit?.SubscribeProducts?.map(
                  (product) => product.name
                ) ?? []
              }
              onChange={() => setMemberSettingsModal(member)}
              isSettingButton={isStaff || member.UserId === user?.id}
            />
          ))}

        {!!members.length && (
          <MembersSummary
            members={members}
            hideRoleCounter={user?.availableSubscriptionRoles}
          />
        )}

        {isMemberSettingsModal && (
          <MemberSettingsModal
            title={`${t("ORGANIZATION_TAB_MEMBERS_SETTINGS_MODAL_TITLE")} ${getFullName(
              isMemberSettingsModal.User.firstName,
              isMemberSettingsModal.User.lastName
            )}`}
            member={isMemberSettingsModal}
            isLastMember={
              members?.filter((item) => item.isRfpEmailNotifications).length < 2
            }
            availableInList={
              organizationDetail?.SearchPreference?.availableIn ??
              user?.ActiveOrganization?.SearchPreference?.availableIn ??
              []
            }
            ownMember={isMemberSettingsModal.UserId === user?.id}
            projectsList={projectsList}
            subscriptions={subscriptions}
            individualSubscriptions={
              isMemberSettingsModal?.User?.SubscribeUserUnit?.SubscribeProducts?.map(
                (product) => product.name
              ) ?? []
            }
            subscriptionRoles={subscriptionRoles}
            clientRole={clientRole}
            onRemove={removeMember}
            onOk={onUpdateMember}
            onClose={() => setMemberSettingsModal(null)}
          />
        )}
      </div>
    );
  })
);
